import React, { createContext, useState, useEffect } from 'react';
import api from '../utils/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let token;
    try {
      token = localStorage.getItem('token');
    } catch (e) {
      setLoading(false);
      return;
    }
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      // Client app uses backend /api/clients/profile (guest users)
      const response = await api.get('/api/clients/profile');
      const client = response.data?.client;
      if (client) {
        setUser({
          id: client.id,
          email: client.email,
          role: 'client',
          fullName: client.email?.split('@')[0],
          phone: client.phone,
          ...client
        });
      }
    } catch (error) {
      try {
        const token = localStorage.getItem('token');
        if (token && token.startsWith('mock_token_')) {
          const mockUserStr = localStorage.getItem('mockUser');
          if (mockUserStr) {
            try {
              const mockUser = JSON.parse(mockUserStr);
              setUser(mockUser);
            } catch (e) {
              localStorage.removeItem('token');
              delete api.defaults.headers.common['Authorization'];
            }
          }
        } else {
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
        }
      } catch (e) {
        delete api.defaults.headers.common['Authorization'];
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // Client app uses backend /api/clients/login (guest users)
      const response = await api.post('/api/clients/login', { email, password });
      const { client, token } = response.data;
      if (!token) throw new Error('No token received');
      const user = client ? {
        id: client.id,
        email: client.email,
        role: 'client',
        fullName: client.email?.split('@')[0],
        phone: client.phone,
        ...client
      } : response.data.user;
      try { localStorage.setItem('token', token); } catch (e) {}
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      return user;
    } catch (error) {
      // If backend is not ready, use mock data for development
      if (error.response?.status === 403 || error.response?.data?.message) {
        throw error;
      }
      console.log('Backend not available, using mock login');
      const mockUser = {
        id: Date.now(),
        name: email.split('@')[0],
        email: email,
        role: 'client',
      };
      const mockToken = 'mock_token_' + Date.now();
      localStorage.setItem('token', mockToken);
      localStorage.setItem('mockUser', JSON.stringify(mockUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${mockToken}`;
      setUser(mockUser);
      return mockUser;
    }
  };

  const register = async (userData) => {
    const isPropertyOwner = userData.role === 'property_owner' || userData.role === 'Property_Owner';

    if (isPropertyOwner) {
      // Property owner (Become a Partner) → POST /api/property-owner/register
      const fullName = (userData.fullName || userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim()).trim();
      const phoneValue = String(userData.countryCode || '').trim() + String(userData.phone || '').trim();
      const payload = {
        fullName: fullName || userData.email?.split('@')[0] || 'Property Owner',
        email: userData.email,
        phone: phoneValue.trim() || (userData.phone || '').trim(),
        password: userData.password,
        nationality: userData.nationality || undefined,
      };
      const response = await api.post('/api/property-owner/register', payload);
      const { message, owner } = response.data;
      // Property owner backend does not return token (email verification required)
      return {
        needsVerification: true,
        message: message || 'Property owner registered successfully. Please check your email to verify your account.',
        email: (owner && owner.email) || userData.email,
        role: 'property_owner',
      };
    }

    // Client registration → POST /api/clients/register
    const phoneValue = String(userData.countryCode || '').trim() + String(userData.phone || '').trim();
    const payload = {
      email: userData.email,
      password: userData.password,
      phone: phoneValue.trim() || (userData.phone || '').trim(),
      dateOfBirth: userData.dateOfBirth || '1990-01-01',
      gender: userData.gender || 'Male',
      firstName: userData.firstName,
      lastName: userData.lastName,
      fullName: userData.fullName || userData.name,
      nationality: userData.nationality,
      countryCode: userData.countryCode
    };
    const isFormData = userData instanceof FormData;
    const response = await api.post('/api/clients/register', isFormData ? userData : payload, isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {});
    const { client, token, message } = response.data;
    if (token) {
      try { localStorage.setItem('token', token); } catch (e) {}
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const user = client ? { id: client.id, email: client.email, role: 'client', fullName: client.email?.split('@')[0], phone: client.phone, ...client } : response.data.user;
      setUser(user);
      return user;
    }
    return { needsVerification: true, message: message || response.data.message, email: (client && client.email) || userData.email };
  };

  const logout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('mockUser');
    } catch (e) {}
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = async (userId, userData) => {
    try {
      const response = await api.put(`/api/users/${userId}`, userData);
      const updatedUser = response.data;
      setUser(updatedUser);
      try {
        const token = localStorage.getItem('token');
        if (token && token.startsWith('mock_token_')) {
          localStorage.setItem('mockUser', JSON.stringify(updatedUser));
        }
      } catch (e) {}
      return updatedUser;
    } catch (error) {
      // If backend is not ready, update mock user
      if (error.message.includes('Network Error') || error.response?.status >= 500) {
        console.log('Backend not available, updating mock user');
        const mockUser = { ...user, ...userData };
        delete mockUser.password; // Don't store password in mock
        delete mockUser.currentPassword;
        delete mockUser.confirmPassword;
        setUser(mockUser);
        localStorage.setItem('mockUser', JSON.stringify(mockUser));
        return mockUser;
      }
      throw error;
    }
  };

  const getDashboardPath = () => {
    if (!user) return '/login';
    const roleMap = {
      super_admin: '/super-admin',
      admin: '/admin',
      operations: '/operations',
      property_owner: '/owner',
      client: '/client',
      housekeeping: '/housekeeping',
    };
    return roleMap[user.role] || '/login';
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, getDashboardPath, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

